/**
 * SessionRestoreProvider — runs once on app boot to silently restore the
 * access token from the stored refresh token.
 *
 * Why this exists:
 *   Access tokens are never persisted (security). On every page reload the
 *   access token is null. Without this provider the first API call would get
 *   a 401, the interceptor would trigger a refresh, and there would be a race
 *   between the loading state and the AuthGuard redirect — causing the user
 *   to see the login page even though their session is still valid.
 *
 * This provider keeps isLoading=true until the restoration attempt finishes,
 * so AuthGuard shows a spinner instead of bouncing the user to /login.
 */

import { type ReactNode, useEffect, useRef } from 'react';

import { restoreSession } from '@api/lib/session-restore';
import { tokenStorage } from '@api/lib/token-storage';
import { useAuthStore } from '@stores/auth-store';

import { authApi } from '@features/auth/services/auth.api';

interface Props {
  children: ReactNode;
}

export function SessionRestoreProvider({ children }: Props) {
  // Guard against StrictMode double-invoke in development.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    // IMPORTANT: Read from getState() — NOT from React hook closures.
    // React hook values are captured at render time and may reflect the
    // initial (pre-rehydration) store state. By the time useEffect runs,
    // Zustand has fully rehydrated from localStorage, so getState() always
    // returns the current rehydrated values.
    const { user, accessToken, updateToken, logout, setLoading } = useAuthStore.getState();

    // Case 1: No persisted user — not logged in, stop loading immediately.
    if (!user) {
      setLoading(false);
      return;
    }

    // Case 2: Access token already in memory (guard for non-reload scenarios).
    if (accessToken) {
      setLoading(false);
      return;
    }

    // Case 3: User persisted but no access token — attempt silent restore.
    void restoreSession({
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSuccess: async (newToken) => {
        updateToken(newToken);

        // Restore permissions: sessionStorage first (fast path), API fallback.
        const { setPermissions } = useAuthStore.getState();
        const cached = tokenStorage.getPermissions();
        if (cached && cached.length > 0) {
          setPermissions(cached);
        } else {
          try {
            const perms = await authApi.fetchPermissions();
            tokenStorage.setPermissions(perms);
            setPermissions(perms);
          } catch {
            // Non-fatal: user can still use the app, permissions default to empty
          }
        }

        setLoading(false);
      },
      onFailure: () => {
        tokenStorage.removePermissions();
        logout();
      },
    });
  }, []);

  return <>{children}</>;
}
